<template>
  <AppLayout>
    <div class="transaction">
      <section id="procurement-analytics">
        <div class="container-fluid">
          
          <div class="row align-items-end mb-4">
            <div class="col-md-6">
              <div class="pd-tab-group">
                <button
                  :class="['pd-tab', activeTab === 'analytics' ? 'pd-tab-active' : '']"
                  @click="activeTab = 'analytics'"
                >
                  Procurement Analytics
                </button>
                <button
                  :class="['pd-tab', activeTab === 'credit' ? 'pd-tab-active' : '']"
                  @click="activeTab = 'credit'"
                >
                  Credit Payments
                </button>
              </div>
            </div>
            <div class="col-md-6 text-end">
              <span class="badge bg-light text-dark border p-2">
                Period: Year to Date {{ currentYear }}
              </span>
            </div>
          </div>

          <!-- Filters 
          <div class="dashboard-filters mb-4 p-3 bg-light rounded">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label small fw-bold">Date Range</label>
                <select v-model="filters.dateRange" class="form-select form-select-sm">
                  <option value="ytd">Year to Date</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-bold">Department</label>
                <select v-model="filters.department" class="form-select form-select-sm">
                  <option value="">All Departments</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-bold">Project</label>
                <input v-model="filters.project" type="text" class="form-control form-control-sm" placeholder="All Projects">
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-bold">Supplier Type</label>
                <select v-model="filters.supplierType" class="form-select form-select-sm">
                  <option value="">All Suppliers</option>
                  <option value="contracted">Contracted</option>
                  <option value="unlisted">Unlisted</option>
                </select>
              </div>
            </div>
          </div>-->

          <!-- Analytics Tab Content -->
          <div v-show="activeTab === 'analytics'">

          <!-- Skeleton loader -->
          <div v-if="loading" class="procurement-skeleton">
            <div class="warehouse-inventory-card-wrapper mb-4">
              <div v-for="n in 4" :key="n" class="warehouse-inventory-card procurement-skeleton-card">
                <div class="skeleton-line skeleton-label"></div>
                <div class="skeleton-line skeleton-value"></div>
              </div>
            </div>
            <div class="row mb-4">
              <div v-for="n in 3" :key="n" class="col-md-4 mb-3">
                <div class="card h-100">
                  <div class="card-body">
                    <div class="skeleton-line skeleton-title"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-else>
            <!-- ============================================================ -->
            <!-- LAYER 1: EXECUTIVE KPIs (SIMPLE) -->
            <!-- ============================================================ -->
            <div class="kpi-layer-1 mb-2">
              <div class="row">
                <!-- Total Spend YTD -->
                <div class="col-md-3 mb-3">
                  <div class="kpi-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <p class="kpi-label text-uppercase">Total Spend (YTD)</p>
                        <h3 class="kpi-value">{{ formatCurrencyShort(summary.totalSpend) }}</h3>
                      </div>
                    </div>
                    <div class="mt-2 small text-muted">Cumulative spend across all categories</div>
                  </div>
                </div>

                <!-- Realised Savings -->
                <div class="col-md-3 mb-3">
                  <div class="kpi-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <p class="kpi-label text-uppercase">Realised Savings</p>
                        <h3 class="kpi-value" style="color: #10b981;">{{ formatCurrencyShort(summary.savings) }}</h3>
                      </div>
                    </div>
                    <div class="mt-2 small text-success">Actual cost reduction achieved</div>
                  </div>
                </div>

                <!-- Foregone Savings -->
                <div class="col-md-3 mb-3">
                  <div class="kpi-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <p class="kpi-label text-uppercase">Foregone Savings</p>
                        <h3 class="kpi-value" style="color: #ef4444;">{{ formatCurrencyShort(summary.foregoneSavings) }}</h3>
                      </div>
                    </div>
                    <div class="mt-2 small text-danger">Missed saving opportunities</div>
                  </div>
                </div>

                <!-- Avg Cycle Time -->
                <div class="col-md-3 mb-3">
                  <div class="kpi-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <p class="kpi-label text-uppercase">Avg Cycle Time</p>
                        <h3 class="kpi-value">{{ summary.avgCycleTime }}<span style="font-size: 0.6em; margin-left: 4px;">days</span></h3>
                      </div>
                    </div>
                    <div class="mt-2 small text-muted">PR to PO approval speed</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- LAYER 2: OPERATIONAL ALERTS -->
            <!-- ============================================================ -->
            <div class="alert-layer-2 mb-5">
              <div class="row">
                <!-- POs Overdue -->
                <div class="col-md-3 mb-3">
                  <div class="alert-card ">
                    <div class="alert-content">
                      <div class="d-flex justify-content-between align-items-center">
                        <p class="alert-label mb-0">POs Overdue</p>
                      </div>
                      <h4 class="alert-value text-danger mt-2">{{ posOverdue }}</h4>
                      <small class="text-muted d-block mt-1">Delivery date has passed </small>
                    </div>
                  </div>
                </div>

                <!-- POs Due This Week -->
                <div class="col-md-3 mb-3">
                  <div class="alert-card">
                    <div class="alert-content">
                      <div class="d-flex justify-content-between align-items-center">
                        <p class="alert-label mb-0">POs Due This Week</p>
                      </div>
                      <h4 class="alert-value text-warning mt-2">{{ posDueThisWeek }}</h4>
                      <small class="text-muted d-block mt-1">Expiring in next 7 days</small>
                    </div>
                  </div>
                </div>

                <!-- POs Due This Month -->
                <div class="col-md-3 mb-3">
                  <div class="alert-card">
                    <div class="alert-content">
                      <div class="d-flex justify-content-between align-items-center">
                        <p class="alert-label mb-0">POs Due This Month</p>
                      </div>
                      <h4 class="alert-value text-primary mt-2">{{ posDueThisMonth }}</h4>
                      <small class="text-muted d-block mt-1">Scheduled for rollout</small>
                    </div>
                  </div>
                </div>

                <!-- Compliance Status -->
                <div class="col-md-3 mb-3">
                  <div class="alert-card">
                    <div class="alert-content">
                      <div class="d-flex justify-content-between align-items-center">
                        <p class="alert-label mb-0">Supplier Compliance</p>
                        <span class="badge bg-success">Good</span>
                      </div>
                      <h4 class="alert-value text-success mt-2">{{ summary.contractedSuppliersPct }}<span style="font-size: 0.6em;">%</span></h4>
                      <small class="text-muted d-block mt-1">Contracted vs Unlisted</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- GROUP 4: ORGANIZATIONAL BREAKDOWN -->
            <!-- ============================================================ -->
            <div class="analytics-group mb-5">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Department</h6>
                      <div v-if="!spendByDepartment.length" class="text-muted text-center py-4" style="font-size:12px;">No department data</div>
                      <div v-else class="procurement-chart-container chart-align-top">
                        <BDBarChart
                          :data="spendByDepartment"
                          categoryKey="department"
                          valueKey="spend"
                          valueLabel="Spend (Ksh)"
                          :colors="['#0284c7']"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Project</h6>
                      <div v-if="!spendByProject.length" class="text-muted text-center py-4" style="font-size:12px;">No project data</div>
                      <div v-else class="procurement-chart-container chart-align-top">
                        <BDBarChart
                          :data="spendByProject"
                          categoryKey="project"
                          valueKey="spend"
                          valueLabel="Spend (Ksh)"
                          :colors="['#0891b2']"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Currency</h6>
                      <div v-if="!spendByCurrency.length" class="text-muted text-center py-4" style="font-size:12px;">No currency data</div>
                      <div v-else class="procurement-chart-container">
                        <BDPieChart
                          :data="spendByCurrency"
                          categoryKey="currency"
                          valueKey="spend"
                          valueLabel="Spend"
                          :colors="['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b']"
                          :pieHole="0.4"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- LAYER 3: ANALYTICS (GROUPED BY MEANING) -->
            <!-- ============================================================ -->

            <!-- GROUP 1: SPEND INSIGHTS -->
            <div class="analytics-group mb-5">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Month (YTD)</h6>
                      <div v-if="!spendByMonth.length" class="text-muted text-center py-4" style="font-size:12px;">No spend data</div>
                      <div v-else class="procurement-chart-container">
                        <BDBarChart
                          :data="spendByMonth"
                          categoryKey="month"
                          valueKey="spend"
                          valueLabel="Spend"
                          :colors="['#0284c7']"
                          :yAxisFormatter="$formatShortMillions"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Category</h6>
                      <div v-if="!spendByCategory.length" class="text-muted text-center py-4" style="font-size:12px;">No spend data</div>
                      <div v-else class="procurement-chart-container">
                        <BDBarChart
                          :data="spendByCategory"
                          categoryKey="category"
                          valueKey="spend"
                          valueLabel="Spend"
                          :colors="['#0369a1']"
                          :yAxisFormatter="$formatShortMillions"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Spend by Value Bracket</h6>
                      <div v-if="!spendByBracket.length" class="text-muted text-center py-4" style="font-size:12px;">No bracket data</div>
                      <div v-else class="procurement-chart-container">
                        <BDPieChart
                          :data="spendByBracket"
                          categoryKey="bracket"
                          valueKey="spend"
                          valueLabel="Spend"
                          :colors="['#f59e0b', '#14b8a6', '#8b5cf6', '#ef4444']"
                          :pieHole="0.4"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- GROUP 2: SUPPLIER INSIGHTS -->
            <div class="analytics-group mb-5">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Suppliers by Category</h6>
                      <div v-if="!supplierCategoryData.length" class="text-muted text-center py-4" style="font-size:12px;">No category data</div>
                      <div v-else class="procurement-chart-container chart-align-top" style="min-height:520px; max-height:640px;">
                        <BDBarChart
                          :data="supplierCategoryData"
                          categoryKey="category"
                          valueKey="share"
                          valueLabel="Supplier share %"
                          orientation="bar"
                          :colors="['#10b981']"
                          :height="Math.max(300, supplierCategoryData.length * 32)"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Top Suppliers by Spend</h6>
                      <div v-if="!topSuppliersBySpend.length" class="text-muted text-center py-4" style="font-size:12px;">No spend data</div>
                      <div v-else class="top-suppliers-list">
                        <div v-for="(supplier, idx) in topSuppliersBySpend.slice(0, 10)" :key="idx" class="supplier-item">
                          <div class="supplier-rank">#{{ idx + 1 }}</div>
                          <div class="supplier-info">
                            <div class="supplier-name">{{ supplier.supplier }}</div>
                            <div class="supplier-spend">{{ formatCurrencyShort(supplier.spend) }}</div>
                          </div>
                          <div class="supplier-badge">
                            <span class="badge">{{ supplier.percentage || calculatePercentage(supplier.spend) }}%</span>
                          </div>
                        </div>
                        <button v-if="topSuppliersBySpend.length > 10" class="btn btn-sm btn-link mt-2">
                          View All ({{ topSuppliersBySpend.length }} suppliers)
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- GROUP 3: EFFICIENCY -->
            <div class="analytics-group mb-5">
              <h6 class="fw-bold text-muted mb-3">Procurement Efficiency</h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Procurement Cycle Time</h6>
                      <small class="text-muted d-block mb-2">Days at each approval stage</small>
                      <div class="procurement-chart-container">
                        <BDBarChart
                          :data="cycleTimeByStage"
                          categoryKey="stage"
                          valueKey="days"
                          valueLabel="Days"
                          :colors="['#e11d48']"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Cycle Length Distribution</h6>
                      <small class="text-muted d-block mb-2">Share of POs by cycle length</small>
                      <div class="procurement-chart-container">
                        <BDPieChart
                          :data="cycleLengthDistribution"
                          categoryKey="length"
                          valueKey="count"
                          valueLabel="POs"
                          :colors="['#0d9488', '#f59e0b', '#e11d48']"
                          :pieHole="0.4"
                        />
                      </div>
                      <div class="mt-3 small text-muted">
                        <div>🟢 Short (≤5 days): <strong>{{ summary.shortCyclePct }}%</strong></div>
                        <div>🟡 Medium (6–15 days): <strong>{{ summary.mediumCyclePct }}%</strong></div>
                        <div>🔴 Long (>15 days): <strong>{{ summary.longCyclePct }}%</strong></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="analytics-group mb-5">
             
              <div class="row">
                <div class="col-md-12 mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <h6 class="fw-bold mb-3">Daily PR and PO Creation (This Week)</h6>
                      
                      <div v-if="prAndPoDaily.length" class="d-flex gap-4 mb-3 small">
                        <div class="d-flex align-items-center">
                          <span style="width: 10px; height: 10px; border-radius: 50%; background: #8b5cf6; display: inline-block; margin-right: 6px;"></span>
                          <span class="text-muted">Purchase Requests</span>
                        </div>
                        <div class="d-flex align-items-center">
                          <span style="width: 10px; height: 10px; border-radius: 50%; background: #10b981; display: inline-block; margin-right: 6px;"></span>
                          <span class="text-muted">Purchase Orders</span>
                        </div>
                      </div>

                      <div v-if="!prAndPoDaily.length" class="text-muted text-center py-4" style="font-size:12px;">No activity data</div>
                      <div v-else class="procurement-chart-container" style="max-height: 400px;">
                        <BDLineChart
                          :data="prAndPoDaily"
                          categoryKey="day"
                          valueKey="prs"
                          :series="[
                            { key: 'prs', label: 'Purchase Requests' },
                            { key: 'pos', label: 'Purchase Orders' }
                          ]"
                          :colors="['#8b5cf6', '#10b981']"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- GROUP 6: AI SUPPLIER RECOMMENDATIONS -->
            <!-- ============================================================ -->
            <div class="analytics-group mb-5">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 class="fw-bold text-muted mb-0">AI Supplier Recommendations</h6>
                <button
                  class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                  :disabled="regenLoading"
                  @click="regenerateRecommendations"
                >
                  <span v-if="regenLoading" class="spinner-border spinner-border-sm" role="status"></span>
                  <span v-else>&#x21bb;</span>
                  {{ regenLoading ? 'Generating...' : 'Regenerate' }}
                </button>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div v-if="!aiRecommendations.length" class="alert alert-info">
                    No recommendations available - Click Regenerate to generate AI-powered insights.
                  </div>
                  <div v-else class="recommendations-grid">
                    <div v-for="(rec, idx) in aiRecommendations" :key="idx" class="recommendation-card">
                      <div class="rec-badge">{{ idx + 1 }}</div>
                      <!-- Priority badge -->
                      <span
                        v-if="rec.priority"
                        :class="['rec-priority-badge', 'rec-priority-' + rec.priority.toLowerCase()]">
                        {{ rec.priority }}
                      </span>
                      <h6 class="fw-bold mb-1 mt-1">{{ rec.title }}</h6>
                      <!-- Estimated savings -->
                      <div v-if="rec.estimatedSavings" class="rec-savings mb-2">
                        Est. savings: <strong>{{ formatCurrencyShort(rec.estimatedSavings) }}/yr</strong>
                      </div>
                      <p class="text-muted small mb-3">{{ rec.description }}</p>
                      <div v-if="rec.actionItems && rec.actionItems.length" class="action-items">
                        <strong class="d-block text-dark mb-2">Action Items:</strong>
                        <ul class="ps-3 mb-0">
                          <li v-for="(item, i) in rec.actionItems" :key="i" class="small">{{ item }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          </div><!-- end analytics tab -->

          <!-- Credit Payments Tab -->
          <CreditPaymentsTab v-if="activeTab === 'credit'" />

        </div>
      </section>
    </div>
  </AppLayout>
</template>

<script>
/* eslint-disable */
import { reactive, ref, onMounted, computed } from 'vue'
import AppLayout from '@/components/AppLayout.vue'
import CreditPaymentsTab from '@/components/CreditPaymentsTab.vue'
import BDBarChart from '@/components/BDBarChart.vue'
import BDPieChart from '@/components/BDPieChart.vue'
import BDLineChart from '@/components/BDLineChart.vue'

const API_BASE = process.env.VUE_APP_URL || 'http://localhost:3000'
function getToken() {
  try {
    const raw = localStorage.getItem('access_token')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed === 'string' ? parsed : (parsed?.access_token ?? parsed?.token ?? null)
  } catch (e) {
    return localStorage.getItem('access_token')
  }
}

export default {
  name: 'ProcurementDashboard',
  components: { AppLayout, CreditPaymentsTab, BDBarChart, BDPieChart, BDLineChart },
  setup(props, { attrs, slots, emit, expose }) {
        // Make global formatter available for chart props
        const formatShortMillions = computed(() => {
          // Use global property if available
          return (typeof getCurrentInstance === 'function' && getCurrentInstance()?.appContext.config.globalProperties.$formatShortMillions)
            ? getCurrentInstance().appContext.config.globalProperties.$formatShortMillions
            : (val) => val;
        });
    const loading      = ref(false)
    const currentYear  = new Date().getFullYear()
    const activeTab    = ref('analytics')

    const filters = reactive({
      dateRange: 'ytd',
      department: '',
      project: '',
      supplierType: '',
    })

    const summary = reactive({
      totalSuppliers:        0,
      contractedSuppliers:   0,
      contractedSuppliersPct:0,
      unlistedSuppliersPct:  0,
      totalSpend:            0,
      savings:               0,
      foregoneSavings:       0,
      avgCycleTime:          0,
      poCount:               0,
      shortCyclePct:         0,
      mediumCyclePct:        0,
      longCyclePct:          0,
    })

    const supplierCategoryData    = ref([])
    const topSuppliersBySpend     = ref([])
    const spendByCategory         = ref([])
    const spendByBracket          = ref([])
    const cycleTimeByStage        = ref([])
    const cycleLengthDistribution = ref([])
    const spendByMonth            = ref([])
    const poCountByMonth          = ref([])
    const spendByDepartment       = ref([])
    const spendByProject          = ref([])
    const spendByCurrency         = ref([])
    const prAndPoDaily            = ref([])
    const aiRecommendations       = ref([])
    const regenLoading            = ref(false)
    const posOverdue              = ref(0)
    const posDueThisWeek          = ref(0)
    const posDueThisMonth         = ref(0)

    const insights = computed(() => {
      const result = []

      if (summary.unlistedSuppliersPct > 50) {
        result.push({
          type: 'warning',
          icon: '⚠️',
          title: 'High Unlisted Supplier Risk',
          message: `${summary.unlistedSuppliersPct}% of suppliers are unlisted → compliance risk. Consider contracting suppliers.`
        })
      }

      if (summary.foregoneSavings > 0) {
        result.push({
          type: 'danger',
          icon: '📉',
          title: 'Foregone Savings',
          message: `${formatCurrency(summary.foregoneSavings)} in potential savings were not realized this year.`
        })
      }

      if (summary.savings > 0) {
        result.push({
          type: 'success',
          icon: '💰',
          title: 'Realised Savings',
          message: `Successfully saved ${formatCurrency(summary.savings)} through procurement optimization.`
        })
      }

      if (summary.longCyclePct > 40) {
        result.push({
          type: 'warning',
          icon: '⏱️',
          title: 'Extended Cycle Times',
          message: `${summary.longCyclePct}% of POs take >15 days. Review approval bottlenecks.`
        })
      }

      return result
    })

    const fetchData = async () => {
      loading.value = true
      try {
        const token = getToken()
        const res  = await fetch(`${API_BASE}/purchases/procurement-analytics`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()

        Object.assign(summary, data.summary ?? {})
        supplierCategoryData.value    = data.supplierCategoryData    ?? []
        topSuppliersBySpend.value     = data.topSuppliersBySpend     ?? []
        spendByCategory.value         = data.spendByCategory         ?? []
        spendByBracket.value          = data.spendByBracket          ?? []
        cycleTimeByStage.value        = (data.cycleTimeByStage ?? []).slice(-3)
        cycleLengthDistribution.value = data.cycleLengthDistribution ?? []
        spendByMonth.value            = data.spendByMonth            ?? []
        poCountByMonth.value          = data.poCountByMonth          ?? []
        spendByDepartment.value       = data.spendByDepartment       ?? []
        spendByProject.value          = data.spendByProject          ?? []
        spendByCurrency.value         = data.spendByCurrency         ?? []
        prAndPoDaily.value            = data.prAndPoDaily            ?? []
        aiRecommendations.value       = data.aiRecommendations?.recommendations ?? []
      } catch (err) {
        console.error('[ProcurementDashboard] Failed to fetch analytics', err)
      } finally {
        loading.value = false
      }
    }

    const computePODueCounts = async () => {
      try {
        const token = getToken()
        const res = await fetch(`${API_BASE}/purchases/dashboard`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        if (!res.ok) return
        const data = await res.json()
        const purchases = data.purchases ?? []
        const now = new Date()
        const today = new Date(now); today.setHours(0, 0, 0, 0)
        const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7); weekEnd.setHours(23, 59, 59, 999)
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); monthEnd.setHours(23, 59, 59, 999)
        let overdue = 0, dueWeek = 0, dueMonth = 0
        for (const p of purchases) {
          const s = (p.status || '').toLowerCase()
          // Overdue: fully approved, delivery date passed, NOT fulfilled/partial
          if (s === 'fully approved' && p.deliveryDate) {
            const deliveryDate = new Date(p.deliveryDate)
            if (!isNaN(deliveryDate.getTime())) {
              if (deliveryDate < today) {
                overdue++
              } else if (deliveryDate >= today && deliveryDate <= weekEnd) {
                dueWeek++
              } else if (deliveryDate > weekEnd && deliveryDate <= monthEnd) {
                dueMonth++
              }
            }
          }
        }
        posOverdue.value = overdue
        posDueThisWeek.value = dueWeek
        posDueThisMonth.value = dueMonth
      } catch (err) {
        console.error('[ProcurementDashboard] Failed to compute PO due counts', err)
      }
    }

    const formatCurrency = (val) => {
      if (val === null || val === undefined) return '—'
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0,
      }).format(val)
    }

    const formatCurrencyShort = (val) => {
      if (val === null || val === undefined || val === 0) return 'Ksh 0'
      const absVal = Math.abs(val)
      let divisor = 1
      let suffix = ''
      if (absVal >= 1000000) {
        divisor = 1000000
        suffix = 'M'
      } else if (absVal >= 1000) {
        divisor = 1000
        suffix = 'K'
      }
      let formatted = (val / divisor).toFixed(1)
      // Remove .0 for whole numbers
      if (formatted.endsWith('.0')) {
        formatted = formatted.slice(0, -2)
      }
      return `Ksh ${formatted}${suffix}`
    }

    const calculatePercentage = (spend) => {
      if (!summary.totalSpend || summary.totalSpend === 0) return 0
      return Math.round((spend / summary.totalSpend) * 100)
    }

    const regenerateRecommendations = async () => {
      regenLoading.value = true
      try {
        const token = getToken()
        const res = await fetch(`${API_BASE}/purchases/ai-recommendations`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        aiRecommendations.value = data.recommendations ?? []
      } catch (err) {
        console.error('[ProcurementDashboard] Failed to regenerate AI recommendations', err)
      } finally {
        regenLoading.value = false
      }
    }

    onMounted(() => { fetchData(); computePODueCounts() })

    return {
      loading,
      currentYear,
      filters,
      summary,
      supplierCategoryData,
      topSuppliersBySpend,
      spendByCategory,
      spendByBracket,
      cycleTimeByStage,
      cycleLengthDistribution,
      spendByMonth,
      poCountByMonth,
      spendByDepartment,
      spendByProject,
      spendByCurrency,
      prAndPoDaily,
      aiRecommendations,
      regenLoading,
      regenerateRecommendations,
      insights,
      formatCurrency,
      formatCurrencyShort,
      calculatePercentage,
      fetchData,
      posOverdue,
      posDueThisWeek,
      posDueThisMonth,
      formatShortMillions,
      activeTab,
    }
  },
}
</script>

<style scoped>
/* ============================================================ */
/* TAB TOGGLE */
/* ============================================================ */
.pd-tab-group {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
}

.pd-tab {
  padding: 8px 20px;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: #f3f4f6;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
}

.pd-tab:hover:not(.pd-tab-active) {
  background: #e5e7eb;
  color: #374151;
}

.pd-tab-active {
  background: #fff;
  color: #1f2937;
  font-weight: 600;
  border-bottom: 2px solid #fff;
  margin-bottom: -2px;
  position: relative;
  z-index: 1;
}

/* ============================================================ */
/* SKELETON LOADER */
/* ============================================================ */
.procurement-skeleton .skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: procurement-skeleton-pulse 1.5s ease-in-out infinite;
}

.procurement-skeleton .skeleton-label {
  height: 12px;
  width: 70%;
  margin-bottom: 10px;
}

.procurement-skeleton .skeleton-value {
  height: 24px;
  width: 50%;
}

.procurement-skeleton .skeleton-title {
  height: 16px;
  width: 140px;
}

.procurement-skeleton-card {
  pointer-events: none;
}

@keyframes procurement-skeleton-pulse {
  0%, 100% { background-position: 200% 0; }
  50% { background-position: -200% 0; }
}

/* ============================================================ */
/* LAYOUT */
/* ============================================================ */
#procurement-analytics .warehouse-inventory-card {
  min-height: 70px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
}

#procurement-analytics .procurement-chart-container {
  min-height: 300px;
  max-height: 480px;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

#procurement-analytics .procurement-chart-container.chart-align-top {
  justify-content: flex-start;
  min-height: 350px;
}

/* ============================================================ */
/* FILTERS */
/* ============================================================ */
.dashboard-filters {
  border: 1px solid #e5e7eb;
}

/* ============================================================ */
/* LAYER 1: EXECUTIVE KPIs (SIMPLE) */
/* ============================================================ */
.kpi-layer-1 .kpi-card {
  padding: 15px;
  border-radius: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.kpi-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

.kpi-value {
  font-size: 15px !important;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
  line-height: 1.2;
}

.kpi-icon {
  font-size: 15px !important;
  background: white;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* ============================================================ */
/* LAYER 2: OPERATIONAL ALERTS */
/* ============================================================ */
.alert-layer-2 .alert-card {
  padding: 16px;
  border-radius: 6px;
  background: white;
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease;
}

.alert-layer-2 .alert-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.alert-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

.alert-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 6px;
  line-height: 1;
  color: #1f2937;
}

/* ============================================================ */
/* INSIGHTS CARDS */
/* ============================================================ */
.insights-section .insight-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid;
  font-size: 12px;
}

.insight-card.insight-warning {
  background: #fffbeb;
  border-left-color: #f59e0b;
}

.insight-card.insight-danger {
  background: #fef2f2;
  border-left-color: #ef4444;
}

.insight-card.insight-success {
  background: #f0fdf4;
  border-left-color: #10b981;
}

.insight-icon {
  font-size: 16px;
}

.insight-text strong {
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
}

.insight-text p {
  margin: 0;
  color: #6b7280;
}

/* ============================================================ */
/* ANALYTICS GROUPS */
/* ============================================================ */
.analytics-group h6 {
  color: #1f2937;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 10px;
}

.card {
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card h6 {
  color: #1f2937;
  font-size: 14px;
}

/* ============================================================ */
/* TOP SUPPLIERS LIST */
/* ============================================================ */
.top-suppliers-list .supplier-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
}

.top-suppliers-list .supplier-item:last-child {
  border-bottom: none;
}

.supplier-rank {
  font-weight: 700;
  color: #667eea;
  min-width: 30px;
  font-size: 12px;
}

.supplier-info {
  flex: 1;
}

.supplier-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.supplier-spend {
  color: #6b7280;
  font-size: 12px;
}

.supplier-badge {
  text-align: right;
}

.supplier-badge .badge {
  background: #1e40af;
  color: #ffffff;
  padding: 4px 8px;
  font-weight: 600;
}

/* ============================================================ */
/* AI RECOMMENDATIONS */
/* ============================================================ */
.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.recommendation-card {
  background: linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.recommendation-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-color: #d1d5db;
  transform: translateY(-2px);
}

.rec-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
}

.rec-priority-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.rec-priority-high   { background: #fee2e2; color: #b91c1c; }
.rec-priority-medium { background: #fef3c7; color: #92400e; }
.rec-priority-low    { background: #d1fae5; color: #065f46; }

.rec-savings {
  font-size: 12px;
  color: #059669;
  background: #ecfdf5;
  border-radius: 4px;
  padding: 3px 8px;
  display: inline-block;
}

.recommendation-card h6 {
  color: #1f2937;
  margin-top: 5px;
  font-size: 14px;
  line-height: 1.5;
}

.recommendation-card .action-items {
  background: white;
  padding: 12px;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.recommendation-card .action-items ul li {
  color: #374151;
  margin-bottom: 6px;
}
</style>
